// components/video-modal.tsx
"use client";

import { useEffect, useState, useRef } from "react";
import dynamic from "next/dynamic";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useSession } from "next-auth/react";

const VideoPlayer = dynamic(() => import("../../video-player"), { ssr: false });

export function VideoModal({ data }: any) {
  const [isOpen, setIsOpen] = useState(false);
  const { status } = useSession();
  const [hasChecked, setHasChecked] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [player, setPlayer] = useState<any>(null);
  const currentStepRef = useRef<number>(currentStep);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Update the ref whenever currentStep changes
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  const formatedData = [];

  if (data?.first_video) {
    formatedData.push({
      video: {
        src: data.first_video,
        type: `video/${data.first_video?.split(".").pop() || "mp4"}`,
      },
    });
  }
  if (data?.second_video) {
    formatedData.push({
      video: {
        src: data.second_video,
        type: `video/${data.second_video?.split(".").pop() || "mp4"}`,
      },
    });
  }

  useEffect(() => {
    // Don't proceed until we know the auth status
    if (status === "loading") return;

    // Don't show modal if user is authenticated
    if (status === "authenticated") {
      setIsOpen(false);
      setHasChecked(true);
      return;
    }
    // Check if this is the first visit
    const hasVisited = document.cookie.includes("visitedBefore=true");

    if (!hasVisited) {
      // Set timer to open modal after 5 seconds
      timerRef.current = setTimeout(() => {
        setIsOpen(true);
        // Set flags to remember the visit
        document.cookie = "visitedBefore=true; path=/; max-age=86400"; // 1 day
      }, 5000); // 5 seconds
    }

    setHasChecked(true);

    // Clean up the timer on unmount
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [status]);

  const handlePlayerReady = (player: any) => {
    setPlayer(player);
  };

  const handleNext = () => {
    if (currentStep < formatedData?.length) {
      navigateToStep(currentStep + 1);
    } else {
      setIsOpen(false);
      // Reset to first step when closing
      setTimeout(() => setCurrentStep(1), 300);
    }
  };

  const navigateToStep = (step: number) => {
    setIsLoading(true);
    // Pause current video
    if (player) {
      player.pause();
    }

    // Simulate loading delay
    setTimeout(() => {
      setCurrentStep(step);
      setIsLoading(false);
    }, 500);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      navigateToStep(currentStep - 1);
    }
  };

  if (!hasChecked) return null;

  const videoJsOptions = {
    autoplay: true,
    controls: true,
    responsive: true,
    fluid: true,
    muted: false,
    sources: [formatedData[currentStep - 1]?.video],
    aspectRatio: "16:9",
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="w-full max-w-[95vw] gap-0 overflow-hidden p-0 sm:max-w-[90vw] md:max-w-[800px]">
        <DialogHeader className="mb-4 px-4 pt-4 text-left sm:px-6 sm:pt-6">
          <DialogTitle className="mb-1 text-xl font-bold sm:mb-2 sm:text-2xl">
            {/* {formatedData[currentStep - 1].title} */}
          </DialogTitle>
        </DialogHeader>
        <div
          className="relative px-2 sm:px-4"
          style={{ paddingBottom: "56.25%" }}
        >
          <div className="absolute inset-0 mx-auto h-full w-[calc(100%-32px)] sm:w-[calc(100%-48px)]">
            {isOpen && (
              <VideoPlayer
                options={videoJsOptions}
                onReady={handlePlayerReady}
              />
            )}
          </div>
        </div>

        <DialogFooter className="flex flex-row items-center justify-between px-4 pb-4 sm:justify-between sm:px-6 sm:pb-6">
          <div className="text-sm font-semibold text-blue-600">
            {currentStep} OF {formatedData?.length}
          </div>
          <div className="flex items-center gap-2">
            {currentStep > 1 && (
              <Button
                variant="outline"
                onClick={handleBack}
                className="px-3 py-1 text-xs sm:px-4 sm:py-2 sm:text-sm"
                disabled={isLoading}
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              className="bg-blue-600 px-3 py-1 text-xs hover:bg-blue-700 sm:px-4 sm:py-2 sm:text-sm"
              disabled={isLoading}
            >
              {isLoading
                ? "Loading..."
                : currentStep < formatedData?.length
                  ? "Continue"
                  : "Close"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
